<template>
  <div class="modal-frame modal-medium active erase-dates" tabindex="-1" role="dialog" aria-modal="true" aria-labelledby="erase-dates-title" @keydown="onKeydown">
    <div class="modal-frame-content">
      <button class="btn-close" type="button" :disabled="busy" :aria-label="$t('modals.cancel')" @click="close"><svg class="svg-cross"><use href="/img/sprite.svg#svg-cross" /></svg></button>
      <div class="modal-frame-header flex-column">
        <div id="erase-dates-title" class="h3">{{ $t('eraseDates.title') }}</div>
        <div v-if="preview" class="label">{{ $t('eraseDates.total', { count: preview.total }) }}</div>
      </div>
      <div class="modal-frame-body erase-dates__body">
        <p class="erase-dates__scope">{{ orderId ? $t('eraseDates.orderScope', { id: orderId }) : $t('pageTitleOrdersAll') }}</p>
        <p v-if="busy" role="status">{{ $t('eraseDates.loading') }}</p>
        <p v-if="error" class="erase-dates__error" role="alert">{{ error }}</p>
        <p v-if="result" class="erase-dates__result" role="status">{{ $t('eraseDates.result', result) }}</p>
        <form v-if="selectedIds.length && !result" id="erase-dates-form" @submit.prevent="apply">
          <div class="erase-dates__fields">
            <label v-for="field in fields" :key="field" class="checkbox-label">
              <input v-model="selected" type="checkbox" :value="field" :disabled="busy">
              <span class="fake-checkbox"></span><span class="checkbox-name">{{ $t(`orderTable.${field}`) }}</span>
            </label>
          </div>
        </form>
        <div class="form-buttons flex-1 erase-dates__footer">
          <button v-if="selectedIds.length && !result" class="btn" type="submit" form="erase-dates-form" :disabled="busy || !preview || !preview.total || !selected.length">{{ $t('eraseDates.title') }}</button>
          <button class="btn-outline" type="button" :disabled="busy" @click="close">{{ $t('eraseDates.close') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import axios from 'axios';
const fields = ['date_arrival_supplier', 'date_ready_shipment', 'date_order_shipment'];
export default {
  props: { orderId: { type: Number, default: 0 }, positionIds: { type: Array, required: true } },
  emits: ['close', 'completed'],
  data() {
    return { fields, selected: [], busy: false, preview: null, result: null, error: '', params: {}, selectedIds: [], routePath: '', active: true };
  },
  watch: { '$route.fullPath'(value) { if (value !== this.routePath) this.$emit('close'); } },
  mounted() {
    this.selectedIds = [...this.positionIds];
    this.routePath = this.$route.fullPath;
    this.params = Object.fromEntries(Object.entries(this.$route.query).filter(([key]) => key.startsWith('f_') || key === 'sort' || key === 'sort_dir'));
    this.returnFocus = document.activeElement;
    this.$nextTick(() => this.$el.focus());
    this.request(true);
  },
  beforeUnmount() { this.active = false; if (this.returnFocus?.isConnected) this.returnFocus.focus(); },
  methods: {
    onKeydown(event) {
      if (event.key === 'Escape' && !this.busy) { event.preventDefault(); this.close(); }
      if (event.key !== 'Tab') return;
      const controls = [...this.$el.querySelectorAll('button:not(:disabled), input:not(:disabled)')];
      if (!controls.length) { event.preventDefault(); return; }
      const first = controls[0], last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    },
    close() { if (this.result) this.$emit('completed'); this.$emit('close'); },
    apply() { if (this.selected.length && this.preview && !this.busy) this.request(false); },
    async request(preview) {
      if (!this.selectedIds.length) {
        this.error = this.$t('eraseDates.noRows');
        return;
      }
      this.busy = true; this.error = '';
      try {
        const body = new FormData();
        body.append('position_ids', JSON.stringify(this.selectedIds));
        body.append('fields', JSON.stringify(preview ? fields : this.selected));
        body.append('preview', preview ? '1' : '0');
        if (!preview) body.append('token', this.preview.token);
        const response = await axios.post(`/api/order/erase-dates/${this.orderId ? `${this.orderId}/` : ''}`, body, { params: this.params });
        if (!this.active || this.$route.fullPath !== this.routePath) return;
        if (response.data.status === 'error') throw new Error((response.data.errors || []).map(item => item.message).join('\n'));
        if (preview) this.preview = response.data.data;
        else this.result = response.data.data;
      } catch (error) {
        if (this.active) this.error = error.message || this.$t('errors.serverError');
      } finally {
        if (this.active) {
          this.busy = false;
          this.$nextTick(() => {
            if (this.active && (document.activeElement === this.$el || !this.$el.contains(document.activeElement))) this.$el.querySelector('button')?.focus();
          });
        }
      }
    },
  },
};
</script>

<style scoped>
.erase-dates__body { display: flex; flex-direction: column; gap: 24px; }
.erase-dates__body p { margin: 0; line-height: 1.5; }
.erase-dates__scope { color: var(--vtorostepennyy-tekst); }
.erase-dates__error { padding: 16px 20px; border-radius: 8px; background: var(--fon-krasnyy); color: var(--krasnyy); }
.erase-dates__result { padding: 16px 20px; border-radius: 8px; background: var(--biryuzovyy-100); }
.erase-dates__fields { display: flex; flex-direction: column; gap: 20px; padding-block: 24px; border-block: 1px solid var(--kontur-obschiy); }
.erase-dates__footer { display: flex; flex-wrap: nowrap; justify-content: space-between; gap: 16px; }
.erase-dates__footer > button { min-width: 0; padding-inline: 16px; }
@media (max-width: 575px) {
  .erase-dates .modal-frame-content { padding: 48px 24px 24px; width: calc(100% - 32px); min-width: 0; }
  .erase-dates__footer { gap: 12px; }
  .erase-dates__footer > button { font-size: 14px; padding-inline: 8px; }
}
.checkbox-label { break-inside: avoid; }
.checkbox-label input:focus-visible + .fake-checkbox {
  outline: 2px solid var(--biryuzovyy-300);
  outline-offset: 3px;
}
</style>
